
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CardHeader, CardTitle } from '@/components/ui/card';
import { ReportData } from '@/types/report';
import { ReportStatusBadge } from './ReportStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle } from 'lucide-react';

type ReportHeaderProps = {
  report: ReportData;
  formatDate: (dateString: string) => string;
  adminControls?: React.ReactNode;
};

export const ReportHeader: React.FC<ReportHeaderProps> = ({ 
  report, 
  formatDate,
  adminControls
}) => {
  const { t } = useTranslation();
  const teachingDate = formatDate(report.teaching_date);
  
  return (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <div>
        <CardTitle className="text-2xl">
          {t('reports.headerTitleWithName', { name: report.student_nickname })}
        </CardTitle>
        <div className="mt-2 flex items-center space-x-2 flex-wrap">
          <span className="text-sm text-gray-500">
            {t('reports.teachingDateLabel')}: {teachingDate} • {t('reports.subjectLabel')}: {report.subject || '-'}
          </span>
          <span className="mx-2">•</span>
          <ReportStatusBadge status={report.status} rejectionReason={report.rejection_reason} />
          
          {report.hours_deducted !== undefined && (
            <>
              <span className="mx-2">•</span>
              <Badge 
                variant={report.hours_deducted ? "outline" : "secondary"}
                className={`flex items-center gap-1 ${report.hours_deducted ? 'text-green-600 border-green-400' : 'text-amber-600 bg-amber-100'}`}
              >
                {report.hours_deducted ? (
                  <>
                    <CheckCircle className="h-3 w-3" />
                    {t('reports.hoursDeducted')}
                  </>
                ) : (
                  <>
                    <Clock className="h-3 w-3" />
                    {t('reports.hoursNotDeducted')}
                  </>
                )}
              </Badge>
            </>
          )}
        </div>
      </div>
      
      {adminControls && (
        <div className="flex items-center">
          {adminControls}
        </div>
      )}
    </CardHeader>
  );
};
